/**
 * Manhattan routing for the edges the tree has no column for.
 *
 * A cross-branch link and a loop-closing link have the same problem: they join
 * two nodes the layout never put next to each other. Drawn as a straight line
 * or a curve they cut across whatever lies between, which is how a readable
 * tree turns into a hairball. Drawn as a right-angled route through reserved
 * lanes — a horizontal one in the gap between two rows, a vertical one in the
 * gap between two columns — they read like circuit traces: complex, but every
 * line is followable.
 *
 * Where a route has to cross a line it does not join, it hops over it. That
 * convention predates every drawing tool here (schematics have used it since
 * the 1950s) and it exists because a crossing and a junction look identical
 * otherwise.
 */

export const CORNER_RADIUS = 16;
export const HOP_RADIUS = 8;

const round2 = (n) => Math.round(n * 100) / 100;

/**
 * Path through axis-aligned waypoints, with rounded corners and hops over the
 * crossings given per segment.
 *
 * @param {Array<{x,y}>} points waypoints, each leg axis-aligned
 * @param {Object} opts
 * @param {number} opts.radius corner radius
 * @param {Object} opts.hops  { [segmentIndex]: number[] } — for a vertical leg,
 *                            the y values it crosses something at; for a
 *                            horizontal leg, the x values.
 */
export function orthPath(points, { radius = CORNER_RADIUS, hops = {} } = {}) {
  if (!points || points.length < 2) return '';
  const parts = [`M ${round2(points[0].x)} ${round2(points[0].y)}`];
  let cursor = points[0];

  for (let i = 1; i < points.length; i += 1) {
    const target = points[i];
    const next = points[i + 1];
    const vertical = Math.abs(target.x - cursor.x) < 0.5;

    // Stop short of a corner so it can be rounded.
    let legEnd = target;
    if (next) {
      const legLen = vertical ? Math.abs(target.y - cursor.y) : Math.abs(target.x - cursor.x);
      const nextLen = Math.abs(next.x - target.x) + Math.abs(next.y - target.y);
      const r = Math.max(0, Math.min(radius, legLen / 2, nextLen / 2));
      if (r > 0.5) {
        legEnd = vertical
          ? { x: target.x, y: target.y - Math.sign(target.y - cursor.y) * r }
          : { x: target.x - Math.sign(target.x - cursor.x) * r, y: target.y };
      }
    }

    parts.push(...legWithHops(cursor, legEnd, vertical, hops[i] || []));

    if (next && (legEnd.x !== target.x || legEnd.y !== target.y)) {
      const nextVertical = Math.abs(next.x - target.x) < 0.5;
      const legLen = vertical ? Math.abs(target.y - cursor.y) : Math.abs(target.x - cursor.x);
      const nextLen = Math.abs(next.x - target.x) + Math.abs(next.y - target.y);
      const r = Math.max(0, Math.min(radius, legLen / 2, nextLen / 2));
      const after = nextVertical
        ? { x: target.x, y: target.y + Math.sign(next.y - target.y) * r }
        : { x: target.x + Math.sign(next.x - target.x) * r, y: target.y };
      parts.push(`Q ${round2(target.x)} ${round2(target.y)} ${round2(after.x)} ${round2(after.y)}`);
      cursor = after;
    } else {
      cursor = legEnd;
    }
  }

  return parts.join(' ');
}

/** One straight leg, arcing over each crossing on the way. */
function legWithHops(from, to, vertical, crossings) {
  const parts = [];
  const dir = vertical ? Math.sign(to.y - from.y) : Math.sign(to.x - from.x);
  const along = (p) => (vertical ? p.y : p.x);

  const usable = (crossings || [])
    .filter((c) => {
      const lo = Math.min(along(from), along(to)) + HOP_RADIUS;
      const hi = Math.max(along(from), along(to)) - HOP_RADIUS;
      return c > lo && c < hi;
    })
    .sort((a, b) => (dir > 0 ? a - b : b - a));

  usable.forEach((c) => {
    if (vertical) {
      parts.push(`L ${round2(to.x)} ${round2(c - dir * HOP_RADIUS)}`);
      // Bulge the same screen side whichever way the route is travelling, so a
      // column of hops reads as one line rather than a zigzag.
      parts.push(`A ${HOP_RADIUS} ${HOP_RADIUS} 0 0 ${dir > 0 ? 1 : 0} ${round2(to.x)} ${round2(c + dir * HOP_RADIUS)}`);
    } else {
      parts.push(`L ${round2(c - dir * HOP_RADIUS)} ${round2(to.y)}`);
      parts.push(`A ${HOP_RADIUS} ${HOP_RADIUS} 0 0 ${dir > 0 ? 0 : 1} ${round2(c + dir * HOP_RADIUS)} ${round2(to.y)}`);
    }
  });

  parts.push(`L ${round2(to.x)} ${round2(to.y)}`);
  return parts;
}

/**
 * Where a spine connector crosses the vertical line x = X, between y0 and y1.
 *
 * Every spine arrow comes from one generator (layouts/organicTree
 * generateArrowPath): a short horizontal run out of the parent's right edge,
 * then one cubic into the child's left edge, with x strictly increasing. That
 * makes the crossing solvable by bisection instead of by sampling the DOM.
 */
export function crossingsOnVertical(pathD, x, y0, y1) {
  const parsed = parseSpinePath(pathD);
  if (!parsed) return [];
  const lo = Math.min(y0, y1);
  const hi = Math.max(y0, y1);
  const out = [];

  const { start, corner, c1, c2, end } = parsed;

  // The straight run out of the parent.
  if (x >= Math.min(start.x, corner.x) && x <= Math.max(start.x, corner.x)) {
    if (start.y > lo && start.y < hi) out.push(start.y);
  }

  // The cubic. x(t) is monotonic here, so bisect.
  if (x > Math.min(corner.x, end.x) && x < Math.max(corner.x, end.x)) {
    const bez = (a, b, c, d, t) => {
      const u = 1 - t;
      return u * u * u * a + 3 * u * u * t * b + 3 * u * t * t * c + t * t * t * d;
    };
    let loT = 0;
    let hiT = 1;
    for (let i = 0; i < 26; i += 1) {
      const mid = (loT + hiT) / 2;
      if (bez(corner.x, c1.x, c2.x, end.x, mid) < x) loT = mid;
      else hiT = mid;
    }
    const y = bez(corner.y, c1.y, c2.y, end.y, (loT + hiT) / 2);
    if (y > lo && y < hi) out.push(y);
  }

  return out;
}

const NUM = '(-?\\d+(?:\\.\\d+)?)';
const SPINE_RE = new RegExp(
  `^M ${NUM},${NUM} L ${NUM},${NUM} C ${NUM},${NUM} ${NUM},${NUM} ${NUM},${NUM}$`,
);

function parseSpinePath(d) {
  if (typeof d !== 'string') return null;
  const m = SPINE_RE.exec(d.trim());
  if (!m) return null;
  const n = m.slice(1).map(Number);
  return {
    start: { x: n[0], y: n[1] },
    corner: { x: n[2], y: n[3] },
    c1: { x: n[4], y: n[5] },
    c2: { x: n[6], y: n[7] },
    end: { x: n[8], y: n[9] },
  };
}
